"use client";
import {
  Feature,
  FeatureCategory,
  FeatureSummary,
} from "@/lib/types/features.ts";
import * as S from "./styles.ts";
import {
  Accordion2,
  Breadcrumbs,
  Button,
  H2,
  H3,
  H4,
  IconBulletPoint,
  P,
  VideoFrame,
} from "@template/components";
import {
  extractBulletPoints,
  htmlToReactComponents,
  stripHtml,
} from "@/lib/utils/html.ts";
import { FeaturesSideMenu } from "./Sidemenu";

interface IFeatureDescriptionSectionComponentProps {
  feature: Feature;
  categories: FeatureCategory[];
  featureList: FeatureSummary[];
}

const FeatureDescriptionSectionComponent = ({
  feature,
  categories,
  featureList,
}: IFeatureDescriptionSectionComponentProps) => {
  const hasVideo = feature?.videos?.length > 0;
  const videos = hasVideo ? feature?.videos : null;

  const questionsAndAnswers = feature?.q_and_as;
  const hasQuestions =
    feature?.q_and_as?.length > 0 && questionsAndAnswers?.length > 0;

  // Call forwarding, call recording, call parking,  cloud storage sync, business sms all have an alt format
  // I suspect this is because they're coming from whitelabel instead of the /frontend nextJS app, not due to the any of the data in the json
  // Have hardcoded the feature ids for now, but will investigate further
  const altFormat = [18, 42, 261, 246, 258, 264, 267].includes(feature?.id);

  const isDomains = feature?.category_id === 6;
  const breadcrumbUrl = isDomains
    ? "/domain-names/registration"
    : "/voice/business-phone-system/";
  const breadCrumbLabel = isDomains ? "Domain Names" : "Business Phone System";

  if (!feature) return null;

  return (
    <S.FeatureDescriptionSectionWrapper>
      <Breadcrumbs
        breadcrumbs={[{ label: breadCrumbLabel, url: breadcrumbUrl }]}
      />
      <S.FeatureDescriptionSectionContainer>
        <S.FeaturesSideMenuWrapper>
          <FeaturesSideMenu
            categories={categories}
            featureList={featureList}
            activeCategoryId={feature?.category_id}
          />
        </S.FeaturesSideMenuWrapper>
        <S.MainContainer>
          <S.FeatureDescriptionHeader>
            <H3 as="h1">{feature?.h1 || stripHtml(feature?.title)}</H3>
            <P>{feature?.sub_title}</P>

            <S.ButtonGroup>
              <Button
                variant="solid"
                size="sm"
                navigation={{
                  href: "/voip/try-free",
                }}
              >
                Try for free
              </Button>
              <Button
                variant="outline"
                size="sm"
                navigation={{
                  href: "/voice",
                }}
              >
                Compare plans
              </Button>
            </S.ButtonGroup>
          </S.FeatureDescriptionHeader>
          <S.FeatureDescriptionBody>
            <S.FeatureDescriptionText>
              {altFormat && (
                <H2>{`What ${feature.id === 267 ? "are" : "is"} ${feature?.title}?`}</H2>
              )}

              {feature?.description &&
                htmlToReactComponents(feature?.description)}
            </S.FeatureDescriptionText>
            <S.FeatureDescriptionBullets $newFormat={altFormat}>
              <H4>{`Benefits of ${feature?.title}`}</H4>
              {feature?.bullets?.length &&
                extractBulletPoints(feature?.bullets).map((bullet, index) => (
                  <IconBulletPoint
                    key={index}
                    item={{
                      label: bullet,
                    }}
                  />
                ))}
            </S.FeatureDescriptionBullets>
            {hasVideo &&
              videos?.map((video, index) => (
                <S.VideoFrameContainer key={index}>
                  <VideoFrame src={video?.embed_url} />
                </S.VideoFrameContainer>
              ))}

            {hasQuestions && (
              <S.RelatedQuestions>
                <H4>
                  {feature.id === 264
                    ? "AI Transcription FAQs"
                    : feature.id === 267
                      ? "AI Summary FAQs"
                      : "Related FAQs"}
                </H4>
                <Accordion2
                  items={questionsAndAnswers?.map((item) => ({
                    title: item?.question,
                    content: <p>{item?.answer}</p>,
                  }))}
                  activeColor="primary"
                />
              </S.RelatedQuestions>
            )}
          </S.FeatureDescriptionBody>
        </S.MainContainer>
      </S.FeatureDescriptionSectionContainer>
    </S.FeatureDescriptionSectionWrapper>
  );
};

export default FeatureDescriptionSectionComponent;
export const options = [
  {
    description: "",
    displayName: "FeatureDescriptionSectionComponent",
    methods: [],
    props: {
      feature: {
        required: true,
        flowType: { name: "Feature" },
        description: "",
      },
      categories: {
        required: true,
        flowType: {
          name: "Array",
          elements: [{ name: "FeatureCategory" }],
          raw: "FeatureCategory[]",
        },
        description: "",
      },
      featureList: {
        required: true,
        flowType: {
          name: "Array",
          elements: [{ name: "FeatureSummary" }],
          raw: "FeatureSummary[]",
        },
        description: "",
      },
    },
  },
];
